// src/excel/builders/listeners/buildWorkbook.js
// Билдер XLSX для сервиса "Слушатели по часам"
//
// ВХОД:
//   XLSX           — объект из sheetjs (передаёт exportXlsx)
//   params = {
//     t, shortcode, stationName, serviceName,
//     startDate, endDate, startTime, endTime,
//     country, rows, total, fmtNumber, locale
//   }
//
// ВЫХОД:
//   Workbook (XLSX.utils.book_new() с добавленным листом "Listeners")
//
export function buildListenersWorkbook(XLSX, params) {
  const {
    t, shortcode, stationName,
    startDate, endDate, startTime, endTime,
    country, rows = [], total, fmtNumber, locale,
    serviceName
  } = params || {};


  const wb = XLSX.utils.book_new();
  const ws = {};
  const merges = [];

  const serviceTitle = serviceName || (t ? t('listeners.title') : 'Listeners');
  const stationTitle = stationName || shortcode || '';
  const countryStr = country || (t ? t('listeners.all') : 'All');
  const madeOnText = (t && t('pdf.made_on')) || 'Report made on Radiomonitor.net';

  let currentRow = 0;

  const brandRow = [[ (t && t('listeners.brand_line_excel')) || 'Radiomonitor.Net' ]];
  XLSX.utils.sheet_add_aoa(ws, brandRow, { origin: { r: currentRow, c: 0 } });
  merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: 1 } });
  const brandAddr = XLSX.utils.encode_cell({ r: currentRow, c: 0 });
  ws[brandAddr] = ws[brandAddr] || {};
  ws[brandAddr].s = {
    font: { bold: true, sz: 20, color: { rgb: '1F2A5A' } },
    fill: { fgColor: { rgb: 'EEF2FF' } },
    alignment: { horizontal: 'left', vertical: 'center' }
  };
  currentRow += 2;

  const metaRows = [
    [serviceTitle],
    ...(stationTitle ? [[stationTitle]] : []),
    [`${t ? t('listeners.from_date') : 'From'}: ${startDate || ''}  ${t ? t('listeners.time_from') : 'Time from'}: ${startTime || ''}`],
    [`${t ? t('listeners.to_date') : 'To'}: ${endDate || ''}  ${t ? t('listeners.time_to') : 'Time to'}: ${endTime || ''}`],
    [`${t ? t('listeners.country') : 'Country'}: ${countryStr}`],
    [madeOnText],
    ['']
  ];

  XLSX.utils.sheet_add_aoa(ws, metaRows, { origin: { r: currentRow, c: 0 } });
  for (let i = 0; i < metaRows.length - 1; i += 1) {
    merges.push({ s: { r: currentRow + i, c: 0 }, e: { r: currentRow + i, c: 1 } });
  }

  const headerRowIndex = currentRow + metaRows.length;
  const HEAD_HOUR = (t ? t('listeners.table.hour') : 'Hour');
  const HEAD_LIST = (t ? t('listeners.table.listeners') : 'Listeners');
  const tableHeader = [[HEAD_HOUR, HEAD_LIST]];

  const tableRows = rows.map(r => [
    r.period ?? '',
    typeof r.total_listeners === 'number' ? r.total_listeners : Number(r.total_listeners || 0)
  ]);

  const totalRow = [
    '',
    typeof total === 'number' ? total : tableRows.reduce((acc, [, n]) => acc + (Number(n) || 0), 0)
  ];

  const tableBlock = [
    ...tableHeader,
    ...tableRows,
    totalRow
  ];

  XLSX.utils.sheet_add_aoa(ws, tableBlock, { origin: { r: headerRowIndex, c: 0 } });

  const totalRowIndex = headerRowIndex + tableBlock.length - 1;

  const headerStyle = {
    font: { bold: true, color: { rgb: '333333' } },
    fill: { fgColor: { rgb: 'F0F0F0' } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: 'D0D0D0' } },
      bottom: { style: 'thin', color: { rgb: 'D0D0D0' } },
      left: { style: 'thin', color: { rgb: 'D0D0D0' } },
      right: { style: 'thin', color: { rgb: 'D0D0D0' } }
    }
  };

  const bodyAlignment = (col) => ({ alignment: { horizontal: col === 1 ? 'right' : 'left', vertical: 'center' } });

  const borderStyle = {
    border: {
      top: { style: 'thin', color: { rgb: 'E0E0E0' } },
      bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
      left: { style: 'thin', color: { rgb: 'E0E0E0' } },
      right: { style: 'thin', color: { rgb: 'E0E0E0' } }
    }
  };

  const encode = XLSX.utils.encode_cell;

  // Title styling
  const serviceAddr = encode({ r: currentRow, c: 0 });
  if (ws[serviceAddr]) {
    ws[serviceAddr].s = {
      font: { bold: true, sz: 18, color: { rgb: '1F1F1F' } },
      alignment: { horizontal: 'left', vertical: 'center' }
    };
  }

  if (stationTitle) {
    const stationAddr = encode({ r: currentRow + 1, c: 0 });
    if (ws[stationAddr]) {
      ws[stationAddr].s = {
        font: { bold: true, sz: 18, color: { rgb: '1F1F1F' } },
        alignment: { horizontal: 'left', vertical: 'center' }
      };
    }
  }

  const metaStart = currentRow + 1 + (stationTitle ? 1 : 0);
  for (let i = 0; i < 3; i += 1) {
    const addr = encode({ r: metaStart + i, c: 0 });
    if (ws[addr]) {
      ws[addr].s = {
        font: { sz: 11, color: { rgb: '4F4F4F' } },
        alignment: { horizontal: 'left', vertical: 'center' }
      };
    }
  }

  const madeOnAddr = encode({ r: metaStart + 3, c: 0 });
  if (ws[madeOnAddr]) {
    ws[madeOnAddr].s = {
      font: { sz: 10, color: { rgb: '666666' } },
      alignment: { horizontal: 'left', vertical: 'center' }
    };
  }

  // Header styles
  for (let col = 0; col < 2; col += 1) {
    const addr = encode({ r: headerRowIndex, c: col });
    if (ws[addr]) {
      ws[addr].s = { ...headerStyle, alignment: headerStyle.alignment };
    }
  }

  // Body rows formatting
  for (let rIdx = headerRowIndex + 1; rIdx <= totalRowIndex; rIdx += 1) {
    for (let cIdx = 0; cIdx < 2; cIdx += 1) {
      const addr = encode({ r: rIdx, c: cIdx });
      if (!ws[addr]) continue;
      ws[addr].s = {
        ...(ws[addr].s || {}),
        ...bodyAlignment(cIdx),
        ...borderStyle,
        font: { ...(ws[addr].s && ws[addr].s.font), sz: 11, color: { rgb: '1F1F1F' } }
      };
    }
  }

  // Total row emphasis
  const totalText = t
    ? t('listeners.total', {
        count: total ?? 0,
        value: fmtNumber ? fmtNumber(total ?? 0, locale) : (total ?? 0)
      })
    : `Total: ${total ?? 0}`;
  const totalAddrLeft = encode({ r: totalRowIndex, c: 0 });
  const totalAddrRight = encode({ r: totalRowIndex, c: 1 });
  ws[totalAddrLeft] = ws[totalAddrLeft] || {};
  ws[totalAddrLeft].v = totalText;
  ws[totalAddrLeft].t = 's';
  ws[totalAddrRight] = ws[totalAddrRight] || {};
  ws[totalAddrRight].v = '';
  ws[totalAddrRight].t = 's';
  merges.push({ s: { r: totalRowIndex, c: 0 }, e: { r: totalRowIndex, c: 1 } });
  for (let cIdx = 0; cIdx < 2; cIdx += 1) {
    const addr = encode({ r: totalRowIndex, c: cIdx });
    if (ws[addr]) {
      ws[addr].s = {
        ...(ws[addr].s || {}),
        font: { bold: true, sz: 12, color: { rgb: '1F1F1F' } },
        ...borderStyle,
        alignment: { horizontal: 'right', vertical: 'center' }
      };
    }
  }

  // Numeric formatting for listeners column
  for (let rIdx = headerRowIndex + 1; rIdx <= totalRowIndex; rIdx += 1) {
    const addr = encode({ r: rIdx, c: 1 });
    if (ws[addr] && typeof ws[addr].v === 'number') {
      ws[addr].t = 'n';
      ws[addr].z = '#,##0';
    }
  }

  ws['!cols'] = [
    { wch: 24 },
    { wch: 26 }
  ];

  ws['!merges'] = merges;

  const filterEndRow = headerRowIndex + tableRows.length;
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({ s: { r: headerRowIndex, c: 0 }, e: { r: filterEndRow, c: 1 } })
  };

  try {
    ws['!freeze'] = { xSplit: 0, ySplit: headerRowIndex + 1 };
  } catch { /* ignore */ }

  XLSX.utils.book_append_sheet(wb, ws, 'Listeners');

  return wb;
}
